import SenderConnection from '../../play/senderConnection.js'
import ReceiverConnection from '../../play/receiverConnection.js'

export default class PrivateConnector {
    constructor() {
        this.senderConnection = null
        this.receiverConnections = new Map() // For admin: multiple connections to players
        this.playerPeerIdToReceiver = new Map() // Map player peer IDs to receiver connections
        this.isConnected = false
        this.adminConnectionId = null
        this.onMessageCallback = null
        this.onConnectCallback = null
        this.onDisconnectCallback = null
        this.connectionId = null
    }

    // Player side: Connect to admin
    async connectToAdmin(adminConnectionId, onMessage, onConnect, onDisconnect) {
        this.adminConnectionId = adminConnectionId
        this.onMessageCallback = onMessage
        this.onConnectCallback = onConnect
        this.onDisconnectCallback = onDisconnect

        // Only initialize if not already done
        if (!this.senderConnection) {
            await this.initializeSenderConnection()
        }
        
        if (this.adminConnectionId && this.senderConnection && this.senderConnection.peer) {
            console.log(`Attempting to connect to admin with ID: ${this.adminConnectionId}`)
            await this.senderConnection.connect(this.adminConnectionId)
        } else {
            console.warn('Sender connection not ready or no admin connection ID')
        }
    }

    // Initialize sender connection (for players connecting to admin)
    async initializeSenderConnection() {
        if (!this.senderConnection) {
            this.senderConnection = new SenderConnection(
                this.onAdminOpen.bind(this),
                this.onAdminMessage.bind(this),
                this.onAdminClose.bind(this),
                this.onConnectionReady.bind(this)
            )
            
            // Wait for the sender connection to be ready
            await new Promise((resolve) => {
                const checkReady = () => {
                    if (this.senderConnection && this.senderConnection.peer && this.senderConnection.peer.id) {
                        resolve()
                    } else {
                        setTimeout(checkReady, 100)
                    }
                }
                checkReady()
            })
        }
    }

    // Admin side: Create receiver connection for incoming players
    createReceiverConnection(characterPeerId, onPlayerOpen, onPlayerMessage, onPlayerClose) {
        // Check if we already have a receiver connection for this character
        if (this.receiverConnections.has(characterPeerId)) {
            console.log(`Receiver connection already exists for character ${characterPeerId}`)
            return this.receiverConnections.get(characterPeerId)
        }
        
        // Create wrapper functions that include the peer ID
        const wrappedOnPlayerOpen = () => {
            console.log(`Character connected to admin with peer ID: ${characterPeerId}`)
            onPlayerOpen(characterPeerId)
        }
        
        const wrappedOnPlayerMessage = (data) => {
            console.log(`Received message from character ${characterPeerId}:`, data)
            onPlayerMessage(data, characterPeerId)
        }
        
        const wrappedOnPlayerClose = () => {
            console.log(`Character disconnected from admin: ${characterPeerId}`)
            onPlayerClose(characterPeerId)
            // Remove from our map when connection closes
            this.receiverConnections.delete(characterPeerId)
        }
        
        const receiverConnection = new ReceiverConnection(
            wrappedOnPlayerOpen,
            wrappedOnPlayerMessage,
            wrappedOnPlayerClose,
            () => {
                console.log(`Receiver connection ready for character ${characterPeerId}`)
                // Get the connection ID after the connection is ready
                this.connectionId = receiverConnection.getId()
            }
        )
        
        this.receiverConnections.set(characterPeerId, receiverConnection)
        return receiverConnection
    }

    // Admin side: Create a new receiver connection for a specific player
    createPlayerReceiverConnection(playerPeerId, onPlayerOpen, onPlayerMessage, onPlayerClose) {
        // Generate a unique ID for this receiver connection
        const receiverId = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        
        // Create wrapper functions that include the player peer ID
        const wrappedOnPlayerOpen = () => {
            console.log(`Player connected to admin with peer ID: ${playerPeerId}`)
            onPlayerOpen(playerPeerId)
        }
        
        const wrappedOnPlayerMessage = (data) => {
            onPlayerMessage(data, playerPeerId)
        }
        
        const wrappedOnPlayerClose = () => {
            console.log(`Player disconnected from admin: ${playerPeerId}`)
            onPlayerClose(playerPeerId)
            // Remove from our map when connection closes
            this.receiverConnections.delete(receiverId)
        }
        
        const receiverConnection = new ReceiverConnection(
            wrappedOnPlayerOpen,
            wrappedOnPlayerMessage,
            wrappedOnPlayerClose,
            () => {
                console.log(`Player receiver connection ready for ${playerPeerId}`)
                // Store the connection ID for this receiver
                this.connectionId = receiverConnection.getId()
            }
        )
        
        this.receiverConnections.set(receiverId, receiverConnection)
        return receiverConnection
    }

    // Admin side: Create a new receiver connection with a unique peer ID
    createNewReceiverConnection(onPlayerOpen, onPlayerMessage, onPlayerClose) {
        // Generate a unique peer ID for this receiver
        const uniquePeerId = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        
        // Create wrapper functions that will capture the peer ID from the connection
        let currentPeerId = null
        
        const wrappedOnPlayerOpen = () => {
            // Since ReceiverConnection calls onPlayerOpen before setting this.conn,
            // we need to wait for the connection to be established
            const checkConnection = () => {
                if (receiverConnection.conn && receiverConnection.conn.peer) {
                    currentPeerId = receiverConnection.conn.peer
                    console.log(`Player connected to admin with peer ID: ${currentPeerId}`)
                    // Store the mapping from player peer ID to receiver connection
                    this.playerPeerIdToReceiver.set(currentPeerId, receiverConnection)
                    onPlayerOpen(currentPeerId)
                } else {
                    // Try again in a short moment
                    setTimeout(checkConnection, 50)
                }
            }
            checkConnection()
        }
        
        const wrappedOnPlayerMessage = (data) => {
            onPlayerMessage(data, currentPeerId)
        }
        
        const wrappedOnPlayerClose = () => {
            console.log(`Player disconnected from admin: ${currentPeerId}`)
            onPlayerClose(currentPeerId)
            // Remove from our maps when connection closes
            this.receiverConnections.delete(uniquePeerId)
            if (currentPeerId) {
                this.playerPeerIdToReceiver.delete(currentPeerId)
            }
            currentPeerId = null
        }
        
        const receiverConnection = new ReceiverConnection(
            wrappedOnPlayerOpen,
            wrappedOnPlayerMessage,
            wrappedOnPlayerClose,
            () => {
                console.log(`New receiver connection ready with ID: ${receiverConnection.getId()}`)
                // Store the connection ID for this receiver
                this.connectionId = receiverConnection.getId()
            }
        )
        
        // Store this receiver connection
        this.receiverConnections.set(uniquePeerId, receiverConnection)
        return receiverConnection
    }

    // Callbacks for sender connection (player side)
    onConnectionReady() {
        
    }

    onAdminOpen() {
        this.isConnected = true
        if (this.onConnectCallback) {
            this.onConnectCallback()
        }
    }

    onAdminMessage(data) {
        if (this.onMessageCallback) {
            this.onMessageCallback(data)
        }
    }

    onAdminClose() {
        this.isConnected = false
        if (this.onDisconnectCallback) {
            this.onDisconnectCallback()
        } else {
            console.warn('No onDisconnectCallback set')
        }
    }

    // Send message to admin (player side)
    sendMessage(message) {
        if (this.senderConnection && this.isConnected) {
            this.senderConnection.send({
                type: 'character_message',
                content: message,
                timestamp: new Date().toISOString()
            })
        } else {
            console.warn('Cannot send message: not connected to admin')
        }
    }

    // Admin side: Send message to specific character
    sendToCharacter(message, characterPeerId) {
        const receiverConnection = this.playerPeerIdToReceiver.get(characterPeerId)
        if (receiverConnection && receiverConnection.conn && receiverConnection.conn.open) {
            receiverConnection.send(message)
        } else {
            console.warn(`No active connection found for character ${characterPeerId}`)
        }
    }

    // Admin side: Send message to all connected characters
    sendToAllCharacters(message) {
        this.playerPeerIdToReceiver.forEach((receiverConnection) => {
            if (receiverConnection.conn && receiverConnection.conn.open) {
                receiverConnection.send(message)
            }
        })
    }

    // Admin side: Get all connected character peer IDs
    getConnectedCharacters() {
        return Array.from(this.playerPeerIdToReceiver.keys())
    }

    // Admin side: Get connection count
    getConnectionCount() {
        return this.playerPeerIdToReceiver.size
    }

    // Admin side: Close connection to specific character
    closeCharacterConnection(characterPeerId) {
        const receiverConnection = this.playerPeerIdToReceiver.get(characterPeerId)
        if (receiverConnection && receiverConnection.conn) {
            receiverConnection.conn.close()
            this.playerPeerIdToReceiver.delete(characterPeerId)
        }
    }

    // Admin side: Close all character connections
    closeAllCharacterConnections() {
        this.receiverConnections.forEach((receiverConnection) => {
            if (receiverConnection.conn) {
                receiverConnection.conn.close()
            }
        })
        this.receiverConnections.clear()
        this.playerPeerIdToReceiver.clear()
    }

    // Disconnect (both player and admin)
    disconnect() {
        if (this.senderConnection) {
            this.senderConnection.close()
            this.isConnected = false
        }
        this.closeAllCharacterConnections()
    }

    // Check if connected (player side)
    getConnectionStatus() {
        return this.isConnected
    }

    // Get admin connection ID (player side)
    getAdminConnectionId() {
        return this.adminConnectionId
    }

    // Get peer connection ID
    getPeerConnectionId() {
        if (this.senderConnection && this.senderConnection.peer) {
            return this.senderConnection.peer.id
        }
        // If no sender connection, try to get from receiver connections
        if (this.receiverConnections.size > 0) {
            const firstReceiver = this.receiverConnections.values().next().value
            if (firstReceiver && firstReceiver.peer && firstReceiver.peer.id) {
                return firstReceiver.peer.id
            }
        }
        return this.connectionId || null
    }
}
